import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { MapPin, MessageSquare, Download, Image, FileText, Maximize, ChevronLeft, ChevronRight, Clock } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { THEMES, ModeToggle } from "../context/ThemeContext";
import Logo from "../components/Logo";
import { useServerSyncedTimer } from "../hooks/useServerSyncedTimer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const calculateTitleSize = (text, hasContent = true) => {
  const len = text?.length || 0;
  if (!hasContent) {
    if (len <= 8) return 'text-[8rem] md:text-[12rem] lg:text-[16rem]';
    if (len <= 15) return 'text-[6rem] md:text-[10rem] lg:text-[14rem]';
    if (len <= 25) return 'text-[4rem] md:text-[7rem] lg:text-[10rem]';
    if (len <= 40) return 'text-[3rem] md:text-[5rem] lg:text-[7rem]';
    return 'text-[2rem] md:text-[4rem] lg:text-[5rem]';
  }
  if (len <= 10) return 'text-5xl md:text-6xl lg:text-7xl';
  if (len <= 20) return 'text-4xl md:text-5xl lg:text-6xl';
  if (len <= 35) return 'text-3xl md:text-4xl lg:text-5xl';
  return 'text-2xl md:text-3xl lg:text-4xl';
};

const calculateQuestionSize = (questions) => {
  const count = questions?.length || 0;
  const avgLen = questions?.reduce((sum, q) => {
    const text = typeof q === 'string' ? q : q.text || '';
    return sum + text.length;
  }, 0) / Math.max(count, 1) || 0;
  if (count === 1) {
    if (avgLen < 50) return { text: 'text-3xl md:text-4xl lg:text-5xl', padding: 'p-8 md:p-10', gap: 'gap-8' };
    if (avgLen < 100) return { text: 'text-2xl md:text-3xl lg:text-4xl', padding: 'p-6 md:p-8', gap: 'gap-6' };
    return { text: 'text-xl md:text-2xl lg:text-3xl', padding: 'p-6 md:p-8', gap: 'gap-6' };
  }
  if (count === 2) {
    if (avgLen < 80) return { text: 'text-2xl md:text-3xl lg:text-4xl', padding: 'p-6 md:p-8', gap: 'gap-6' };
    return { text: 'text-xl md:text-2xl lg:text-3xl', padding: 'p-5 md:p-6', gap: 'gap-5' };
  }
  if (count <= 3) return { text: 'text-xl md:text-2xl lg:text-3xl', padding: 'p-5 md:p-6', gap: 'gap-4' };
  if (count <= 5) return { text: 'text-lg md:text-xl lg:text-2xl', padding: 'p-4 md:p-5', gap: 'gap-3' };
  return { text: 'text-base md:text-lg lg:text-xl', padding: 'p-3 md:p-4', gap: 'gap-2' };
};

const calculateInstructionsSize = (text) => {
  const len = text?.length || 0;
  if (len < 100) return 'text-3xl md:text-4xl lg:text-5xl';
  if (len < 200) return 'text-2xl md:text-3xl lg:text-4xl';
  if (len < 400) return 'text-xl md:text-2xl lg:text-3xl';
  if (len < 600) return 'text-lg md:text-xl lg:text-2xl';
  return 'text-base md:text-lg lg:text-xl';
};

// Single station slide component
const StationSlide = ({ station, index, totalStations, sessionName, themeConfig }) => {
  const questions = station.questions || [];
  const contentType = station.content_type || "questions";
  const hasInstructions = !!station.instructions?.trim();
  const showQuestionsOnScreen = !station.hide_questions_on_screen;
  const hasContent = (
    hasInstructions ||
    (showQuestionsOnScreen && contentType === "questions" && questions.length > 0) ||
    (contentType === "open_text" && station.open_text?.trim())
  );
  const titleSizeClass = calculateTitleSize(station?.name, hasContent);
  const questionSizes = calculateQuestionSize(questions);
  const instructionsSizeClass = calculateInstructionsSize(station?.instructions);
  const openTextSizeClass = calculateInstructionsSize(station?.open_text);

  return (
    <div
      className="w-screen h-screen flex-shrink-0 flex flex-col relative overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #0D0118 0%, #1a0a2e 50%, #0D0118 100%)' }}
      data-testid={`station-slide-${index}`}
    >
      {/* Background blurs */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full opacity-15 blur-[200px]"
        style={{ backgroundColor: themeConfig.colors.primary }} />
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] rounded-full opacity-15 blur-[200px]"
        style={{ backgroundColor: themeConfig.colors.secondary }} />

      {/* Header */}
      <div className="relative z-10 flex justify-between items-center p-8 md:p-12">
        <div className="flex items-center gap-4">
          <Logo size="lg" />
          <span className="text-[#E2D9F3] text-xl md:text-2xl hidden md:block">{sessionName}</span>
        </div>
        <div className="px-6 py-3 rounded-full" style={{ background: themeConfig.colors.gradient }}>
          <span className="text-white font-bold text-lg md:text-xl">
            Station {index + 1} of {totalStations}
          </span>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-4 md:px-12 lg:px-16 overflow-hidden">
        {/* Station Name */}
        <div className={`text-center ${hasContent ? 'mb-6 md:mb-10' : 'flex-1 flex flex-col items-center justify-center'}`}>
          {hasContent && (
            <div className="flex items-center justify-center gap-4 mb-4">
              <MapPin className="w-8 h-8 md:w-12 md:h-12" style={{ color: themeConfig.colors.accent }} />
            </div>
          )}
          <h1
            className={`${titleSizeClass} font-bold leading-none tracking-tight`}
            style={{
              background: themeConfig.colors.gradient,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}
          >
            {station.name}
          </h1>
          {!hasContent && (
            <div className="mt-8 flex items-center justify-center gap-3">
              <MapPin className="w-8 h-8" style={{ color: themeConfig.colors.accent }} />
              <span className="text-[#E2D9F3] text-xl md:text-2xl">Station {index + 1}</span>
            </div>
          )}
        </div>

        {/* Instructions - shown whenever present (intro text above questions) */}
        {hasInstructions && (
          <div className="w-full max-w-5xl mb-6 md:mb-8 flex-shrink-0">
            <div className="glass-heavy rounded-3xl p-6 md:p-10 max-w-5xl mx-auto">
              <div className="flex items-center justify-center gap-3 mb-4 md:mb-6">
                <FileText className="w-6 h-6 md:w-8 md:h-8" style={{ color: themeConfig.colors.accent }} />
                <h2 className="text-xl md:text-2xl font-semibold text-white">Instructions</h2>
              </div>
              <div className={`text-white ${instructionsSizeClass} leading-relaxed whitespace-pre-wrap text-center`}>
                {station.instructions}
              </div>
            </div>
          </div>
        )}

        {/* Questions */}
        {showQuestionsOnScreen && contentType === "questions" && questions.length > 0 && (
          <div className="w-full max-w-6xl flex-1 flex flex-col min-h-0">
            {questions.length > 1 && (
              <div className="flex items-center justify-center gap-3 mb-4 md:mb-6 flex-shrink-0">
                <MessageSquare className="w-6 h-6 md:w-8 md:h-8" style={{ color: themeConfig.colors.secondary }} />
                <h2 className="text-xl md:text-2xl font-semibold text-white">Discussion Questions</h2>
              </div>
            )}
            <div className={`space-y-2 md:${questionSizes.gap} overflow-y-auto flex-1`}>
              {questions.map((question, idx) => (
                <div
                  key={idx}
                  className={`glass-heavy rounded-2xl ${questionSizes.padding} flex items-start ${questionSizes.gap}`}
                >
                  <div className="w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ background: themeConfig.colors.gradient }}>
                    <span className="text-white text-lg md:text-xl font-bold">{idx + 1}</span>
                  </div>
                  <p className={`text-white ${questionSizes.text} leading-relaxed`}>
                    {typeof question === 'string' ? question : question.text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Open text */}
        {contentType === "open_text" && station.open_text && (
          <div className="w-full flex-1 flex items-center justify-center">
            <div className="glass-heavy rounded-3xl p-8 md:p-12 max-w-5xl">
              <div className={`text-white ${openTextSizeClass} leading-relaxed whitespace-pre-wrap text-center`}>
                {station.open_text}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="relative z-10 text-center pb-8">
        <p className="text-[#6B5E7C] text-lg">Powered by Reshapers</p>
      </div>
    </div>
  );
};

const StationView = () => {
  const { sessionId, stationIndex } = useParams();
  const [session, setSession] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [activeIndex, setActiveIndex] = useState(parseInt(stationIndex) || 0);
  const scrollRef = useRef(null);
  const containerRef = useRef(null);

  // Synced timer derived from session.server_time + timer_end_time
  const timer = useServerSyncedTimer(session);
  const timerRemaining = timer.active ? timer.seconds : null;

  useEffect(() => {
    const fetchSession = async () => {
      try {
        const response = await axios.get(`${API}/sessions/${sessionId}`);
        setSession(response.data);
      } catch (error) {
        console.error("Error fetching session:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchSession();
  }, [sessionId]);

  // Poll session every 2s (timer + content updates)
  useEffect(() => {
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/sessions/${sessionId}`);
        setSession(res.data);
      } catch {}
    };
    const interval = setInterval(poll, 2000);
    return () => clearInterval(interval);
  }, [sessionId]);

  // Scroll to initial station on load
  useEffect(() => {
    if (session && scrollRef.current) {
      const initialIndex = parseInt(stationIndex) || 0;
      scrollRef.current.scrollTo({ left: initialIndex * window.innerWidth, behavior: 'instant' });
      setActiveIndex(initialIndex);
    }
  }, [session, stationIndex]);

  // Track scroll position to update active index
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const idx = Math.round(el.scrollLeft / window.innerWidth);
          setActiveIndex(idx);
          ticking = false;
        });
        ticking = true;
      }
    };
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, [session]);

  // Auto-hide controls (desktop only - mouse-driven). On touch screens the controls stay visible.
  useEffect(() => {
    const isTouchDevice = typeof window !== 'undefined' && (('ontouchstart' in window) || navigator.maxTouchPoints > 0);
    if (isTouchDevice) {
      setShowControls(true);
      return;
    }
    let timeout;
    const handleMouseMove = () => {
      setShowControls(true);
      clearTimeout(timeout);
      timeout = setTimeout(() => setShowControls(false), 3000);
    };
    window.addEventListener('mousemove', handleMouseMove);
    timeout = setTimeout(() => setShowControls(false), 3000);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      clearTimeout(timeout);
    };
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        scrollToStation(activeIndex + 1);
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        scrollToStation(activeIndex - 1);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [activeIndex]);

  const scrollToStation = (index) => {
    const total = session?.stations?.length || 0;
    const clamped = Math.max(0, Math.min(total - 1, index));
    scrollRef.current?.scrollTo({ left: clamped * window.innerWidth, behavior: 'smooth' });
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const exportAsImage = async () => {
    setIsExporting(true);
    try {
      const slideEl = scrollRef.current?.children?.[activeIndex];
      if (!slideEl) return;
      const canvas = await html2canvas(slideEl, {
        scale: 2, backgroundColor: '#0D0118',
        width: 1920, height: 1080, windowWidth: 1920, windowHeight: 1080,
      });
      const link = document.createElement('a');
      const station = session.stations[activeIndex];
      link.download = `station-${activeIndex + 1}-${station?.name || 'unnamed'}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success("Image exported!");
    } catch (error) {
      toast.error("Failed to export image");
    } finally {
      setIsExporting(false);
    }
  };

  const exportAsPDF = async () => {
    setIsExporting(true);
    try {
      const slideEl = scrollRef.current?.children?.[activeIndex];
      if (!slideEl) return;
      const canvas = await html2canvas(slideEl, {
        scale: 2, backgroundColor: '#0D0118',
        width: 1920, height: 1080, windowWidth: 1920, windowHeight: 1080,
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'px', format: [1920, 1080] });
      pdf.addImage(imgData, 'PNG', 0, 0, 1920, 1080);
      const station = session.stations[activeIndex];
      pdf.save(`station-${activeIndex + 1}-${station?.name || 'unnamed'}.pdf`);
      toast.success("PDF exported!");
    } catch (error) {
      toast.error("Failed to export PDF");
    } finally {
      setIsExporting(false);
    }
  };

  const exportAllStations = async () => {
    if (!session?.stations?.length) return;
    setIsExporting(true);
    try {
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'px', format: [1920, 1080] });
      for (let i = 0; i < session.stations.length; i++) {
        const slideEl = scrollRef.current?.children?.[i];
        if (!slideEl) continue;
        if (i > 0) pdf.addPage([1920, 1080], 'landscape');
        const canvas = await html2canvas(slideEl, {
          scale: 2, backgroundColor: '#0D0118',
          width: 1920, height: 1080, windowWidth: 1920, windowHeight: 1080,
        });
        pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, 1920, 1080);
      }
      pdf.save(`all-stations-${session.name || 'session'}.pdf`);
      toast.success("All stations exported as PDF!");
    } catch (error) {
      toast.error("Failed to export all stations");
    } finally {
      setIsExporting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="h-screen w-screen bg-[#0D0118] flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-[#E91E8C] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!session?.stations?.length) {
    return (
      <div className="h-screen w-screen bg-[#0D0118] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-white mb-4">No Stations Found</h1>
          <p className="text-[#E2D9F3]">This session has no stations.</p>
        </div>
      </div>
    );
  }

  const totalStations = session.stations.length;
  const sessionTheme = session?.theme || 'reshapers';
  const themeConfig = THEMES[sessionTheme] || THEMES.reshapers;

  return (
    <div ref={containerRef} className="h-screen w-screen overflow-hidden relative">
      {/* Mode Toggle */}
      <div className="fixed top-4 right-4 z-50">
        <ModeToggle />
      </div>

      {/* Timer Overlay */}
      {timerRemaining !== null && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="fixed top-6 left-1/2 -translate-x-1/2 z-50"
          data-testid="station-timer"
        >
          <div
            className={`flex items-center gap-3 px-6 py-3 rounded-full backdrop-blur-xl shadow-2xl border ${
              timerRemaining <= 30
                ? 'bg-red-500/20 border-red-500/40'
                : timerRemaining <= 60
                ? 'bg-amber-500/20 border-amber-500/40'
                : 'bg-black/40 border-white/10'
            }`}
          >
            <Clock className={`w-6 h-6 ${
              timerRemaining <= 30 ? 'text-red-400' : timerRemaining <= 60 ? 'text-amber-400' : 'text-white'
            }`} />
            <span className={`text-4xl font-bold font-mono tabular-nums ${
              timerRemaining <= 30 ? 'text-red-400' : timerRemaining <= 60 ? 'text-amber-400' : 'text-white'
            }`}>
              {Math.floor(timerRemaining / 60)}:{(timerRemaining % 60).toString().padStart(2, '0')}
            </span>
          </div>
        </motion.div>
      )}

      {/* Horizontal scroll container */}
      <div
        ref={scrollRef}
        className="flex h-full w-full overflow-x-auto overflow-y-hidden snap-x snap-mandatory"
        style={{
          scrollSnapType: 'x mandatory',
          WebkitOverflowScrolling: 'touch',
          scrollBehavior: 'smooth',
          touchAction: 'pan-x',
          overscrollBehaviorX: 'contain',
        }}
        data-testid="station-scroll-container"
      >
        {session.stations.map((station, index) => (
          <div key={station.id} style={{ scrollSnapAlign: 'start' }}>
            <StationSlide
              station={station}
              index={index}
              totalStations={totalStations}
              sessionName={session.name}
              themeConfig={themeConfig}
            />
          </div>
        ))}
      </div>

      {/* Dot indicators (larger touch target via padding) */}
      <div
        className="fixed bottom-24 md:bottom-28 left-1/2 -translate-x-1/2 z-40 flex items-center gap-1 bg-black/30 backdrop-blur-md rounded-full px-2 py-1"
        data-testid="station-dots"
      >
        {session.stations.map((_, i) => (
          <button
            key={i}
            onClick={() => scrollToStation(i)}
            aria-label={`Go to station ${i + 1}`}
            className="min-w-[44px] min-h-[44px] p-3 flex items-center justify-center touch-manipulation active:scale-95 transition-transform"
            data-testid={`station-dot-${i}`}
          >
            <span
              className={`block rounded-full transition-all duration-300 ${
                i === activeIndex
                  ? 'w-10 h-3'
                  : 'w-3 h-3 bg-white/40 hover:bg-white/60'
              }`}
              style={i === activeIndex ? { background: themeConfig.colors.gradient } : undefined}
            />
          </button>
        ))}
      </div>

      {/* Edge swipe / tap zones for mobile (transparent, only visible affordance via cursor) */}
      <button
        type="button"
        aria-label="Previous station"
        onClick={() => scrollToStation(activeIndex - 1)}
        disabled={activeIndex === 0}
        className="md:hidden fixed left-0 top-1/4 bottom-32 w-12 z-30 disabled:opacity-0 active:bg-white/10 transition-colors touch-manipulation flex items-center justify-start pl-1"
        data-testid="station-edge-prev"
      >
        <ChevronLeft className="w-6 h-6 text-white/50" />
      </button>
      <button
        type="button"
        aria-label="Next station"
        onClick={() => scrollToStation(activeIndex + 1)}
        disabled={activeIndex >= totalStations - 1}
        className="md:hidden fixed right-0 top-1/4 bottom-32 w-12 z-30 disabled:opacity-0 active:bg-white/10 transition-colors touch-manipulation flex items-center justify-end pr-1"
        data-testid="station-edge-next"
      >
        <ChevronRight className="w-6 h-6 text-white/50" />
      </button>

      {/* Controls Overlay */}
      <motion.div
        initial={{ opacity: 1 }}
        animate={{ opacity: showControls ? 1 : 0 }}
        className="station-controls fixed bottom-0 left-0 right-0 p-3 md:p-6 bg-gradient-to-t from-black/85 to-transparent z-50"
        style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
      >
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-3 flex-wrap">
          {/* Navigation */}
          <div className="flex items-center gap-2 md:gap-4">
            <Button
              onClick={() => scrollToStation(activeIndex - 1)}
              disabled={activeIndex === 0}
              variant="outline"
              className="btn-ghost min-h-[48px] min-w-[48px] px-3 md:px-4 touch-manipulation"
              aria-label="Previous station"
              data-testid="prev-station-btn"
            >
              <ChevronLeft className="w-5 h-5 md:mr-1" />
              <span className="hidden md:inline">Previous</span>
            </Button>
            <span className="text-white font-mono text-sm md:text-base px-1" data-testid="station-counter">
              {activeIndex + 1} / {totalStations}
            </span>
            <Button
              onClick={() => scrollToStation(activeIndex + 1)}
              disabled={activeIndex >= totalStations - 1}
              variant="outline"
              className="btn-ghost min-h-[48px] min-w-[48px] px-3 md:px-4 touch-manipulation"
              aria-label="Next station"
              data-testid="next-station-btn"
            >
              <span className="hidden md:inline">Next</span>
              <ChevronRight className="w-5 h-5 md:ml-1" />
            </Button>
          </div>

          {/* Export Controls — hidden on small touch screens; available on tablet+ */}
          <div className="hidden md:flex items-center gap-3">
            <Button onClick={exportAsImage} disabled={isExporting} variant="outline" className="btn-ghost min-h-[48px]">
              <Image className="w-4 h-4 mr-2" />PNG
            </Button>
            <Button onClick={exportAsPDF} disabled={isExporting} variant="outline" className="btn-ghost min-h-[48px]">
              <FileText className="w-4 h-4 mr-2" />PDF
            </Button>
            <Button onClick={exportAllStations} disabled={isExporting} className="btn-primary-gradient min-h-[48px]">
              <Download className="w-4 h-4 mr-2" />
              {isExporting ? "Exporting..." : "Export All (PDF)"}
            </Button>
            <Button onClick={toggleFullscreen} variant="outline" className="btn-ghost min-h-[48px] min-w-[48px]" aria-label="Toggle fullscreen">
              <Maximize className="w-5 h-5" />
            </Button>
          </div>

          {/* Compact fullscreen toggle for mobile */}
          <Button onClick={toggleFullscreen} variant="outline" className="md:hidden btn-ghost min-h-[48px] min-w-[48px] touch-manipulation" aria-label="Toggle fullscreen">
            <Maximize className="w-5 h-5" />
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

export default StationView;
